(() => {
  const stateKey = 'lumosCartRegionHooks';
  const existing = window[stateKey];

  // Cart markup can be rendered by the layout, an Ajax section response, and
  // the Theme Editor. Keep one controller alive so each source registers only
  // one set of document listeners.
  if (existing && existing.version === 1) {
    existing.boot();
    return;
  }

  const recommendationSelector = '.cart-product-recommendations';
  const imageFixSelector = '.parent-remove-hover';
  const requests = new Map();
  let disposed = false;

  const isConnected = (element) => Boolean(element && element.isConnected !== false);

  const contains = (root, element) => {
    if (!root || !element) return false;
    if (root === element) return true;
    return typeof root.contains === 'function' && root.contains(element);
  };

  const elementsIn = (root, selector) => {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    const elements = [];
    if (root.nodeType === 1 && typeof root.matches === 'function' && root.matches(selector)) {
      elements.push(root);
    }
    root.querySelectorAll(selector).forEach((element) => elements.push(element));
    return elements;
  };

  const fixAlternateImages = (root) => {
    elementsIn(root, imageFixSelector).forEach((element) => {
      if (!isConnected(element) || typeof element.closest !== 'function') return;
      const wrapper = element.closest('.ProductItem__ImageWrapper--withAlternateImage');
      if (wrapper && wrapper.classList) {
        wrapper.classList.remove('ProductItem__ImageWrapper--withAlternateImage');
      }
    });
  };

  const requestDetails = (element) => {
    const data = element && element.dataset;
    if (!data) return null;

    const value = (name) => data[name] == null ? '' : String(data[name]).trim();
    const productId = value('productId');
    const baseUrl = value('baseUrl');
    const limit = value('limit');
    if (!productId || !baseUrl) return null;

    try {
      const url = new URL(baseUrl, document.baseURI || window.location.href);
      url.searchParams.set('section_id', 'cart-product-recommendations');
      url.searchParams.set('product_id', productId);
      if (limit) url.searchParams.set('limit', limit);
      return { key: url.toString(), url: url.toString() };
    } catch (error) {
      return null;
    }
  };

  const cancel = (element, state) => {
    if (state && state.controller) state.controller.abort();
    if (requests.get(element) === state) requests.delete(element);
  };

  const cleanupDisconnected = () => {
    for (const [element, state] of requests) {
      if (!isConnected(element)) cancel(element, state);
    }
  };

  const isAbort = (error, state) => {
    if (state && state.controller && state.controller.signal && state.controller.signal.aborted) return true;
    return Boolean(error && error.name === 'AbortError');
  };

  const writeRecommendations = (element, state, html) => {
    if (requests.get(element) !== state || !isConnected(element)) return;
    if (state.markup !== element.innerHTML) return;
    const current = requestDetails(element);
    if (!current || current.key !== state.key) return;

    const container = document.createElement('div');
    container.innerHTML = html;
    const replacement = container.querySelector(recommendationSelector);
    if (!replacement || requests.get(element) !== state || !isConnected(element)) return;

    // Preserve the host wrapper and its data attributes. Only the contents of
    // the existing recommendation element are owned by this request.
    element.innerHTML = replacement.innerHTML;
    state.status = 'done';
    fixAlternateImages(element);
  };

  const loadRecommendations = (element) => {
    if (!isConnected(element)) return;
    const details = requestDetails(element);
    if (!details || typeof window.fetch !== 'function') return;

    const previous = requests.get(element);
    if (previous && previous.key === details.key) {
      if (previous.status !== 'pending' || previous.markup === element.innerHTML) return;
    }
    if (previous) cancel(element, previous);

    const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    const state = {
      controller,
      key: details.key,
      markup: element.innerHTML,
      status: 'pending'
    };
    requests.set(element, state);

    const options = { credentials: 'same-origin' };
    if (controller) options.signal = controller.signal;

    let request;
    try {
      request = window.fetch(details.url, options);
    } catch (error) {
      requests.delete(element);
      return;
    }

    Promise.resolve(request)
      .then((response) => {
        if (!response || response.ok === false || typeof response.text !== 'function') {
          throw new Error('Cart recommendations request failed');
        }
        return response.text();
      })
      .then((html) => writeRecommendations(element, state, html))
      .catch((error) => {
        if (requests.get(element) !== state) return;
        if (isAbort(error, state)) {
          requests.delete(element);
          return;
        }
        requests.delete(element);
      });
  };

  const process = (root) => {
    if (disposed) return;
    cleanupDisconnected();
    const scope = root || document;
    fixAlternateImages(scope);
    elementsIn(scope, recommendationSelector).forEach(loadRecommendations);
  };

  const unload = (root) => {
    if (!root) return;
    for (const [element, state] of requests) {
      if (contains(root, element)) cancel(element, state);
    }
  };

  const handleCartRendered = (event) => {
    const detail = event && event.detail;
    if (detail && detail.element) process(detail.element);
  };

  const handleSectionLoad = (event) => process(event && event.target);
  const handleSectionUnload = (event) => unload(event && event.target);

  const boot = () => process(document);
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const [element, state] of requests) cancel(element, state);
    document.removeEventListener('cart:rendered', handleCartRendered);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    window.removeEventListener('DOMContentLoaded', boot);
    if (window[stateKey] === api) delete window[stateKey];
  };

  const api = {
    version: 1,
    boot,
    process,
    unload,
    dispose
  };

  window[stateKey] = api;
  document.addEventListener('cart:rendered', handleCartRendered);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
